import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cloneDeep from 'lodash/cloneDeep'
import { RadioGroup } from 'transfix-ui/components/RadioGroup'
import { ICommodity, ICommodityState, ICarrierState } from 'types/carrier'
import { closeCommoditiesDialog, editCarrierCommodities } from 'utils/actions/carrier'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { NeedPermissionModal } from 'apps/carrier/components/NeedPermissionModal'
import {
  verifications,
  VERIFICATION_KEYS,
  IVerificationKeys,
} from 'apps/carrier/constants/commodities'
import { IStore } from 'apps/carrier/utils/reducers'
import { FormModal } from 'transfix-ui'

interface ISelector {
  carrier: ICarrierState
  carrierCommodities: ICommodityState
}

const { covered, excluded, notVerified } = VERIFICATION_KEYS as IVerificationKeys

const CommoditiesDialog = () => {
  const dispatch = useDispatch()
  const closeDialog = () => {
    dispatch(closeCommoditiesDialog())
  }
  const {
    carrier: { carrierDetails },
    carrierCommodities: { commodities, error, loading, showDialog },
  } = useSelector<IStore, ISelector>(({ carrier, carrierCommodities }) => ({
    carrier,
    carrierCommodities,
  }))
  const { userPermissions } = useUserDetails()
  const editPermitted = userPermissions?.canEditCarrierInsuranceCommoditiesCoverages

  const [dialogCommodities, setDialogCommodities] = useState<ICommodity[]>([])
  useEffect(() => {
    if (showDialog) {
      setDialogCommodities(cloneDeep(commodities))
    }
  }, [showDialog, commodities])

  const setCommodity = (e: React.FormEvent<HTMLInputElement>) => {
    const selectedDescription = e.currentTarget.name
    const selectedValue = e.currentTarget.value
    const newCommodities = dialogCommodities.map((commodity: ICommodity) => {
      if (commodity.description === selectedDescription) {
        let newValue = null
        if (selectedValue === covered) {
          newValue = true
        }
        if (selectedValue === excluded) {
          newValue = false
        }
        commodity.covered = newValue
      }
      return commodity
    })
    setDialogCommodities(newCommodities)
  }

  const saveCommodities = () =>
    dispatch(editCarrierCommodities(carrierDetails!.id, dialogCommodities))

  return editPermitted ? (
    <FormModal
      title='Edit Commodities Covered by Insurance'
      size={6}
      open={showDialog}
      onClose={closeDialog}
      error={error}
      dataTest='modal-edit-commodities-covered-by-insurance'
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          disabled: loading,
        },
        {
          label: 'Save',
          type: 'confirm',
          disabled: loading,
          action: saveCommodities,
        },
      ]}>
      {dialogCommodities.map(commodity => {
        let value = notVerified

        if (commodity.covered === null) {
          value = notVerified
        } else if (commodity.covered) {
          value = covered
        } else {
          value = excluded
        }

        return (
          <div className='pv2' key={`${commodity.id}-${commodity.description}`}>
            <RadioGroup
              label={commodity.description}
              name={commodity.description}
              required={false}
              onOptionChange={setCommodity}
              checked={value}
              options={verifications.map(verification => ({
                ...verification,
                id: `${commodity.id}-${verification.value}`,
              }))}
              key={commodity.id}
            />
          </div>
        )
      })}
    </FormModal>
  ) : (
    <NeedPermissionModal
      action='edit commodities covered by insurance'
      contact='Compliance'
      open={showDialog}
      onClose={closeDialog}
    />
  )
}

export default CommoditiesDialog
